import { countries, usStates, canadianProvinces } from './settings/settingsLocations';
import { settingsTranslator } from '../translations/settings';
import React, { useState, useEffect } from 'react';
import { 
  User, 
  Bell, 
  Lock, 
  Mail, 
  Globe, 
  Smartphone,
  ChevronRight,
  Save,
  X,
  Plug,
  Building2,
  Copy,
  Check,
  Package,
  Cloud,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Loader2
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import Integrations from './Integrations';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { settingsNotifications } from './settings/settingsNotifications';
import { useLanguage } from '../contexts/LanguageContext';

const UserSettings = ({ user, onUpdateProfile, onChangePassword, onBack }) => {
  // Language context
  const { language, setLanguage } = useLanguage();
  const t = settingsTranslator(language);
  const { showSuccess, showError } = settingsNotifications(language);
  
  const [activeSection, setActiveSection] = useState('personal-details');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Organization settings state
  const [organization, setOrganization] = useState(null);
  const [orgForm, setOrgForm] = useState({ 
    name: '', 
    logo_url: '', 
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: ''
  });
  const [orgLoading, setOrgLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Azure DevOps Addon state
  const [azureAddonStatus, setAzureAddonStatus] = useState({ enabled: false, config: {} });
  const [azureAddonLoading, setAzureAddonLoading] = useState(false);
  const [azureConfigForm, setAzureConfigForm] = useState({
    organization_url: '',
    pat_token: ''
  });
  const [testingConnection, setTestingConnection] = useState(false);

  // Fetch organization data when opening organization settings
  useEffect(() => {
    if (activeSection === 'organization' && user && (user?.role === 'owner' || user?.role === 'admin')) {
      fetchOrganization();
    }
  }, [activeSection, user]);

  // Fetch Azure DevOps addon status when opening addons section
  useEffect(() => {
    if (activeSection === 'addons' && user && (user?.role === 'owner' || user?.role === 'admin')) {
      fetchAzureAddonStatus();
    }
  }, [activeSection, user]);

  const fetchAzureAddonStatus = async () => {
    try {
      setAzureAddonLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/addons/azure-devops/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAzureAddonStatus(response.data);
      if (response.data.config?.organization_url) {
        setAzureConfigForm(prev => ({
          ...prev,
          organization_url: response.data.config.organization_url
        }));
      }
    } catch (error) {
      console.error('Failed to fetch Azure DevOps addon status:', error);
      if (error.response?.status === 403) {
        // Plan not supported - that's okay, the section shouldn't be visible anyway
      }
    } finally {
      setAzureAddonLoading(false);
    }
  };

  const handleEnableAzureAddon = async () => {
    if (!azureConfigForm.organization_url || !azureConfigForm.pat_token) {
      showError(t("Please fill in all fields"));
      return;
    }

    try {
      setAzureAddonLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/addons/azure-devops/enable`,
        azureConfigForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(t("Azure DevOps addon enabled successfully!"));
      setAzureConfigForm(prev => ({ ...prev, pat_token: '' }));
      await fetchAzureAddonStatus();
    } catch (error) {
      console.error('Failed to enable Azure DevOps addon:', error);
      showError(t(error.response?.data?.detail) || t("Failed to enable addon"));
    } finally {
      setAzureAddonLoading(false);
    }
  };

  const handleDisableAzureAddon = async () => {
    try {
      setAzureAddonLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/addons/azure-devops/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(t("Azure DevOps addon disabled"));
      await fetchAzureAddonStatus();
    } catch (error) {
      console.error('Failed to disable Azure DevOps addon:', error);
      showError(t(error.response?.data?.detail) || t("Failed to disable addon"));
    } finally {
      setAzureAddonLoading(false);
    }
  };

  const handleTestAzureConnection = async () => {
    try {
      setTestingConnection(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/addons/azure-devops/test-connection`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showSuccess(t(response.data.message));
      } else {
        showError(t(response.data.message));
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      showError(t(error.response?.data?.detail) || t("Connection test failed"));
    } finally {
      setTestingConnection(false);
    }
  };

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching organization from:', `${BACKEND_URL}/api/team/organization`);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/team/organization`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Organization response:', response.data);
      
      if (response.data) {
        setOrganization(response.data);
        setOrgForm({
          name: response.data.name || '',
          logo_url: response.data.logo_url || '',
          address_line1: response.data.address_line1 || '',
          address_line2: response.data.address_line2 || '',
          city: response.data.city || '',
          state_province: response.data.state_province || '',
          postal_code: response.data.postal_code || '',
          country: response.data.country || ''
        });
        console.log('Organization data loaded:', response.data.name);
      } else {
        console.log('No organization data returned');
      }
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      console.error('Error details:', error.response?.data);
      if (error.response?.status === 404) {
        console.log('Organization not found - will be created on first save');
      }
    }
  };

  const settingsCategories = [
    {
      title: t("Personal Settings"),
      items: [
        {
          id: 'personal-details',
          label: t("Personal details"),
          icon: User,
          description: t("Update your name and contact information")
        },
        {
          id: 'security',
          label: t("Password & Security"),
          icon: Lock,
          description: t("Manage your password and security settings")
        },
        {
          id: 'notifications',
          label: t("Notifications"),
          icon: Bell,
          description: t("Configure how you receive notifications")
        }
      ]
    },
    {
      title: t("Account Settings"),
      items: [
        {
          id: 'preferences',
          label: t("Preferences"),
          icon: Globe,
          description: t("Language, timezone, and display settings")
        },
        {
          id: 'integrations',
          label: t("Integrations"),
          icon: Plug,
          description: t("Connect external tools and services")
        }
      ]
    },
    ...(user?.role === 'owner' || user?.role === 'admin' ? [{
      title: t("Organization"),
      items: [
        {
          id: 'organization',
          label: t("Organization Settings"),
          icon: Building2,
          description: t("Manage organization name and details")
        }
      ]
    }] : []),
    // Addons section - only for Business/Enterprise admins
    ...((user?.role === 'owner' || user?.role === 'admin') && 
        ['business', 'business_trial', 'enterprise', 'BUSINESS', 'BUSINESS_TRIAL', 'ENTERPRISE'].includes(user?.plan) ? [{
      title: t("Enterprise Addons"),
      items: [
        {
          id: 'addons',
          label: t("Addons"),
          icon: Package,
          description: t("Enable enterprise integrations and import tools")
        }
      ]
    }] : [])
  ];

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage({ type: '', text: '' });

      if (activeSection === 'personal-details') {
        await onUpdateProfile({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        });
        showSuccess(t("Profile updated successfully!"));
      } else if (activeSection === 'security') {
        // Validate passwords
        if (!formData.currentPassword) {
          setMessage({ type: 'error', text: 'Please enter your current password' });
          setIsSaving(false);
          return;
        }
        
        if (!formData.newPassword) {
          setMessage({ type: 'error', text: 'Please enter a new password' });
          setIsSaving(false);
          return;
        }
        
        if (formData.newPassword.length < 6) {
          setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
          setIsSaving(false);
          return;
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
          setMessage({ type: 'error', text: 'Passwords do not match' });
          setIsSaving(false);
          return;
        }
        
        // Call password change handler
        if (onChangePassword) {
          await onChangePassword(
            formData.currentPassword,
            formData.newPassword,
            formData.confirmPassword
          );
          
          // Clear password fields after successful change
          setFormData({
            ...formData,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          setMessage({ type: 'success', text: 'Password changed successfully!' });
        } else {
          setMessage({ type: 'error', text: 'Password change is not available' });
        }
      }

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message || t("Failed to save changes") });
    } finally {
      setIsSaving(false);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'personal-details':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Personal details")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Manage your personal information")}
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">{t("Full Name")}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t("John Doe")}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">{t("Email Address")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("This email is used for login and notifications")}
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">{t("Phone Number (Optional)")}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Password & Security")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Manage your password and security preferences")}
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="currentPassword">{t("Current Password")}</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                    placeholder={t("Enter current password")}
                  />
                </div>

                <div>
                  <Label htmlFor="newPassword">{t("New Password")}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    placeholder={t("Enter new password")}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Use at least 8 characters with a mix of letters, numbers, and symbols")}
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">{t("Confirm New Password")}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder={t("Confirm new password")}
                  />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Notifications")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Configure how you receive notifications")}
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">{t("Email Notifications")}</p>
                    <p className="text-sm text-gray-500">{t("Receive updates via email")}</p>
                  </div>
                  <input type="checkbox" className="toggle" defaultChecked />
                </div>

                <div className="flex items-center justify-between py-3 border-b">
                  <div>
                    <p className="font-medium">{t("Test Failure Alerts")}</p>
                    <p className="text-sm text-gray-500">{t("Get notified when tests fail")}</p>
                  </div>
                  <input type="checkbox" className="toggle" defaultChecked />
                </div>

                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium">{t("Weekly Reports")}</p>
                    <p className="text-sm text-gray-500">{t("Receive weekly summary reports")}</p>
                  </div>
                  <input type="checkbox" className="toggle" />
                </div>
              </div>
            </Card>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Preferences")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Customize your experience")}
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-6">
                <div>
                  <Label htmlFor="settings-language">{t("Language")}</Label>
                  <select id="settings-language"
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      showSuccess(settingsTranslator(e.target.value)(e.target.value === 'es' ? 'Language changed to Spanish' : 'Language changed to English'), e.target.value);
                    }}
                  >
                    <option value="en">{t("English")}</option>
                    <option value="es">{t("Spanish (Español)")}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Dashboard will be displayed in selected language")}
                  </p>
                </div>

                <div>
                  <Label>{t("Timezone")}</Label>
                  <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md">
                    <option value="UTC-8 (Pacific Time)">{t("UTC-8 (Pacific Time)")}</option>
                    <option value="UTC-5 (Eastern Time)">{t("UTC-5 (Eastern Time)")}</option>
                    <option value="UTC+0 (GMT)">{t("UTC+0 (GMT)")}</option>
                  </select>
                </div>

                <div>
                  <Label>{t("Date Format")}</Label>
                  <select className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md">
                    <option value="MM/DD/YYYY">{t("MM/DD/YYYY")}</option>
                    <option value="DD/MM/YYYY">{t("DD/MM/YYYY")}</option>
                    <option value="YYYY-MM-DD">{t("YYYY-MM-DD")}</option>
                  </select>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'integrations':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Integrations")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Connect external tools and services to enhance your workflow")}
              </p>
            </div>
            <Integrations />
          </div>
        );

      case 'organization':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Organization Settings")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Manage your organization name and branding")}
              </p>
            </div>

            <Card className="p-6">
              <div className="space-y-4">
                {/* Organization ID - Read Only */}
                {organization?.id && (
                  <div>
                    <Label htmlFor="org-id">{t("Organization ID")}</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id="org-id"
                        type="text"
                        value={organization.id}
                        readOnly
                        className="bg-gray-50 cursor-default"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(organization.id);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                          showSuccess(t("Organization ID copied to clipboard"));
                        }}
                        className="flex-shrink-0"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            {t("Copied")}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            {t("Copy")}
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {t("Unique identifier for your organization")}
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="org-name">{t("Organization Name *")}</Label>
                  <Input
                    id="org-name"
                    type="text"
                    placeholder={t("Enter organization name")}
                    value={orgForm.name}
                    onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                    disabled={orgLoading}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t("Your organization name displayed across the platform")}
                  </p>
                </div>

                {/* Business Address Section */}
                <div className="space-y-4 pt-2">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">{t("Business Address (for invoicing)")}</h4>
                  </div>

                  <div>
                    <Label htmlFor="address-line1">{t("Address Line 1")}</Label>
                    <Input
                      id="address-line1"
                      type="text"
                      placeholder={t("Street address, building, P.O. box")}
                      value={orgForm.address_line1}
                      onChange={(e) => setOrgForm({ ...orgForm, address_line1: e.target.value })}
                      disabled={orgLoading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="address-line2">{t("Address Line 2 (Optional)")}</Label>
                    <Input
                      id="address-line2"
                      type="text"
                      placeholder={t("Apartment, suite, unit, floor")}
                      value={orgForm.address_line2}
                      onChange={(e) => setOrgForm({ ...orgForm, address_line2: e.target.value })}
                      disabled={orgLoading}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">{t("City")}</Label>
                      <Input
                        id="city"
                        type="text"
                        placeholder={t("City")}
                        value={orgForm.city}
                        onChange={(e) => setOrgForm({ ...orgForm, city: e.target.value })}
                        disabled={orgLoading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="state-province">{t("State / Province")}</Label>
                      {orgForm.country === 'United States' ? (
                        <select
                          id="state-province"
                          value={orgForm.state_province}
                          onChange={(e) => setOrgForm({ ...orgForm, state_province: e.target.value })}
                          disabled={orgLoading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">{t("Select State")}</option>
                          {usStates.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      ) : orgForm.country === 'Canada' ? (
                        <select
                          id="state-province"
                          value={orgForm.state_province}
                          onChange={(e) => setOrgForm({ ...orgForm, state_province: e.target.value })}
                          disabled={orgLoading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">{t("Select Province")}</option>
                          {canadianProvinces.map(province => (
                            <option key={province} value={province}>{province}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          id="state-province"
                          type="text"
                          placeholder="State / Province / Region"
                          value={orgForm.state_province}
                          onChange={(e) => setOrgForm({ ...orgForm, state_province: e.target.value })}
                          disabled={orgLoading}
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="postal-code">{t("Postal / ZIP Code")}</Label>
                      <Input
                        id="postal-code"
                        type="text"
                        placeholder={t("Postal or ZIP code")}
                        value={orgForm.postal_code}
                        onChange={(e) => setOrgForm({ ...orgForm, postal_code: e.target.value })}
                        disabled={orgLoading}
                      />
                    </div>

                    <div>
                      <Label htmlFor="country">{t("Country *")}</Label>
                      <select
                        id="country"
                        value={orgForm.country}
                        onChange={(e) => {
                          setOrgForm({ 
                            ...orgForm, 
                            country: e.target.value,
                            state_province: '' // Reset state when country changes
                          });
                        }}
                        disabled={orgLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">{t("Select Country")}</option>
                        {countries.map(country => (
                          <option key={country} value={country}>{t(country)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="org-logo">{t("Logo URL (Optional)")}</Label>
                  <Input
                    id="org-logo"
                    type="url"
                    placeholder="https://example.com/logo.png"
                    value={orgForm.logo_url}
                    onChange={(e) => setOrgForm({ ...orgForm, logo_url: e.target.value })}
                    disabled={orgLoading}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t("URL to your organization logo image")}
                  </p>
                </div>

                {orgForm.logo_url && (
                  <div>
                    <Label>{t("Logo Preview")}</Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                      <img
                        src={orgForm.logo_url}
                        alt={t("Organization logo")}
                        className="h-16 w-auto object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          showError(t("Failed to load logo image"));
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={async () => {
                      if (!orgForm.name.trim()) {
                        showError(t("Organization name is required"));
                        return;
                      }

                      setOrgLoading(true);
                      try {
                        const token = localStorage.getItem('token');
                        console.log('Saving organization:', orgForm);
                        
                        const response = await axios.put(
                          `${BACKEND_URL}/api/team/organization`,
                          orgForm,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        console.log('Save response:', response.data);
                        await fetchOrganization();
                        showSuccess(t("Organization settings updated successfully!"));
                      } catch (error) {
                        console.error('Failed to update organization:', error);
                        console.error('Error response:', error.response?.data);
                        showError(t(error.response?.data?.detail) || t("Failed to update organization"));
                      } finally {
                        setOrgLoading(false);
                      }
                    }}
                    disabled={orgLoading || !orgForm.name.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {orgLoading ? (
                      <>{t("Saving...")}</>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t("Save Changes")}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );

      case 'addons':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">{t("Enterprise Addons")}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {t("Enable enterprise-level integrations and import tools")}
              </p>
            </div>

            {/* Azure DevOps Import Addon */}
            <Card className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Cloud className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t("Azure DevOps Import")}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("Import work items, test plans, test suites, and test cases from Azure DevOps")}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        Business & Enterprise
                      </Badge>
                      {azureAddonStatus.enabled && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {t("Enabled")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {azureAddonStatus.enabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisableAzureAddon}
                    disabled={azureAddonLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    {t("Disable")}
                  </Button>
                )}
              </div>

              {azureAddonLoading && !azureAddonStatus.enabled ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : !azureAddonStatus.enabled ? (
                <div className="space-y-4 border-t pt-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">{t("Setup Instructions")}</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>{t("Go to Azure DevOps → User Settings → Personal Access Tokens")}</li>
                          <li>{t("Create a new token with")} <strong>Work Items (Read)</strong> {t("and")} <strong>Test Management (Read)</strong> {t("scopes")}</li>
                          <li>{t("Copy your organization URL (e.g., https://dev.azure.com/yourorg)")}</li>
                          <li>{t("Paste both values below and enable the addon")}</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    <div>
                      <Label htmlFor="azure-org-url">{t("Organization URL *")}</Label>
                      <Input
                        id="azure-org-url"
                        type="url"
                        placeholder="https://dev.azure.com/your-organization"
                        value={azureConfigForm.organization_url}
                        onChange={(e) => setAzureConfigForm(prev => ({ ...prev, organization_url: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t("Your Azure DevOps organization URL")}</p>
                    </div>

                    <div>
                      <Label htmlFor="azure-pat">{t("Personal Access Token *")}</Label>
                      <Input
                        id="azure-pat"
                        type="password"
                        placeholder={t("Enter your PAT token")}
                        value={azureConfigForm.pat_token}
                        onChange={(e) => setAzureConfigForm(prev => ({ ...prev, pat_token: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">{t("Token is stored securely and never shared")}</p>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleEnableAzureAddon}
                      disabled={azureAddonLoading || !azureConfigForm.organization_url || !azureConfigForm.pat_token}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {azureAddonLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t("Connecting...")}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {t("Enable Addon")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 border-t pt-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">{t("Connected to Azure DevOps")}</p>
                        <p className="text-xs text-green-700 mt-1">
                          {t("Organization:")} {azureAddonStatus.config?.organization_url}
                        </p>
                        {azureAddonStatus.enabled_at && (
                          <p className="text-xs text-green-600 mt-1">
                            {t("Enabled by {name} on {date}", { name: azureAddonStatus.enabled_by, date: new Date(azureAddonStatus.enabled_at).toLocaleDateString(language === 'es' ? 'es' : 'en') })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{t("Test Connection")}</p>
                      <p className="text-xs text-gray-500">{t("Verify your Azure DevOps connection is working")}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestAzureConnection}
                      disabled={testingConnection}
                    >
                      {testingConnection ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t("Testing...")}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {t("Test Connection")}
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Download className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{t("Import Data")}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {t("Go to")} <strong>Sprint QA</strong> {t("to import work items, test plans, and test cases from Azure DevOps. Look for the \"Import from Azure DevOps\" button in the sidebar.")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Update credentials section */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">{t("Update Credentials")}</p>
                    <div className="grid gap-4">
                      <div>
                        <Label htmlFor="azure-org-url-update">{t("Organization URL")}</Label>
                        <Input
                          id="azure-org-url-update"
                          type="url"
                          placeholder={azureAddonStatus.config?.organization_url || "https://dev.azure.com/your-organization"}
                          value={azureConfigForm.organization_url}
                          onChange={(e) => setAzureConfigForm(prev => ({ ...prev, organization_url: e.target.value }))}
                        />
                      </div>

                      <div>
                        <Label htmlFor="azure-pat-update">{t("New Personal Access Token")}</Label>
                        <Input
                          id="azure-pat-update"
                          type="password"
                          placeholder={t("Enter new PAT token to update")}
                          value={azureConfigForm.pat_token}
                          onChange={(e) => setAzureConfigForm(prev => ({ ...prev, pat_token: e.target.value }))}
                        />
                      </div>

                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={handleEnableAzureAddon}
                          disabled={azureAddonLoading || !azureConfigForm.pat_token}
                        >
                          {azureAddonLoading ? t("Updating...") : t("Update Credentials")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Placeholder for future addons */}
            <Card className="p-6 opacity-60">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gray-100 rounded-lg">
                  <Package className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-500">{t("More Addons Coming Soon")}</h3>
                  <p className="text-sm text-gray-400 mt-1">
                    {t("Additional enterprise integrations will be available here")}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-semibold text-gray-900">{t("Settings")}</h1>
            <Button variant="ghost" size="sm" onClick={onBack}>
              <X className="h-4 w-4 mr-2" />
              {t("Close")}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-6 sticky top-24">
              {settingsCategories.map((category) => (
                <div key={category.title}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    {category.title}
                  </h3>
                  <div className="space-y-1">
                    {category.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveSection(item.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between group transition-colors ${
                            isActive
                              ? 'bg-purple-50 text-purple-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Icon className={`h-4 w-4 ${isActive ? 'text-purple-600' : 'text-gray-400'}`} />
                            <span className={`text-sm font-medium ${isActive ? 'text-purple-700' : 'text-gray-900'}`}>
                              {item.label}
                            </span>
                          </div>
                          {isActive && <ChevronRight className="h-4 w-4 text-purple-600" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-3">
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                {t(message.text)}
              </div>
            )}

            {renderSection()}

            {/* Save Button */}
            {(activeSection === 'personal-details' || activeSection === 'security') && (
              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {isSaving ? (
                    <>{t("Saving...")}</>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {t("Save Changes")}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
